import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * Sticky bottom register-interest bar.
 *
 * Lives on the long /research/:slug landing pages (and any other page
 * that opts in). Mobile-first: pinned to the bottom of the viewport
 * on small screens once the user has scrolled past the hero, so the
 * "Register interest" action is never more than a thumb away.
 *
 * Hidden by default on desktop (the in-page hero CTA is enough) and
 * dismissable on mobile (state persists for the session).
 */
export default function StickyRegisterCTA({
  to = "/register",
  label = "Register your interest",
  microcopy = "Two minutes · Free · No spam",
  threshold = 480,
}) {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem("meditalk:sticky-cta-dismissed") === "1";
  });

  useEffect(() => {
    if (dismissed) return;
    const onScroll = () => {
      setVisible(window.scrollY > threshold);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold, dismissed]);

  if (dismissed) return null;

  return (
    <div
      className={`md:hidden fixed left-3 right-3 bottom-3 z-40 transition-all duration-300 ${
        visible
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-6 pointer-events-none"
      }`}
      data-testid="sticky-register-cta"
    >
      <div className="rounded-2xl bg-[var(--dark-bg)] border border-white/10 shadow-2xl shadow-black/30 backdrop-blur px-3 py-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-white leading-tight">
            Be first in line for the next study
          </div>
          <div className="text-[11px] text-white/65 leading-tight mt-0.5 truncate">
            {microcopy}
          </div>
        </div>
        <Link
          to={to}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] font-bold text-sm flex-shrink-0 hover:bg-[var(--lime-dark)] active:scale-95 transition-all"
          data-testid="sticky-register-cta-btn"
        >
          {label}
          <ArrowRight size={14} strokeWidth={2.5} />
        </Link>
        <button
          type="button"
          onClick={() => {
            sessionStorage.setItem("meditalk:sticky-cta-dismissed", "1");
            setDismissed(true);
          }}
          aria-label="Dismiss"
          className="text-white/40 hover:text-white/80 text-xl leading-none px-1 flex-shrink-0"
          data-testid="sticky-register-cta-dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
}
